import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Unable to sign in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex items-center justify-center bg-ivory px-6 py-12">
        <div className="w-full max-w-md">
          <Logo />
          <div className="mt-12">
            <h1 className="font-serif text-4xl font-bold tracking-tight text-navy-900">Welcome back</h1>
            <p className="mt-3 text-navy-500">
              Sign in to manage the wellbeing tools, courses and resources for your Resilience Community clients.
            </p>
          </div>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <Label htmlFor="email" className="text-navy-800">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@rcc-life.com.au"
                className="mt-1.5 h-12 rounded-xl border-navy-100 bg-white focus-visible:ring-gold-500"
                required
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-navy-800">Password</Label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-sm font-medium text-gold-600 hover:text-gold-700"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1.5 h-12 rounded-xl border-navy-100 bg-white focus-visible:ring-gold-500"
                required
              />
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="h-12 w-full rounded-xl bg-gold-500 text-white hover:bg-gold-600 font-semibold text-base shadow-ambient"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 text-xs leading-relaxed text-navy-400">
            Client accounts are managed securely inside your GoHighLevel portal. This dashboard is for RCC administrators only.
          </p>
        </div>
      </div>

      {/* Right: banner */}
      <div className="relative hidden overflow-hidden bg-[#FBF6EC] lg:block">
        <img
          src="/leaf-banner.png"
          alt="Soft green leaves on a warm cream background"
          className="absolute inset-0 h-full w-full object-cover object-right"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FBF6EC] via-[#FBF6EC]/20 to-transparent" />
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#FBF6EC] to-transparent" />
        <div className="absolute bottom-0 left-0 p-14">
          <h2 className="font-serif text-5xl font-bold leading-tight text-navy-900">Owning My Journey</h2>
          <p className="mt-4 max-w-md text-lg text-navy-600">My wellbeing. My choices. My next step.</p>
        </div>
      </div>
    </div>
  );
}
